import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { GroomingQueueState } from "@ttc-spectree/toolbox-contract";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarClock, Clock, Link as LinkIcon, Presentation, Repeat, Sparkles } from "lucide-react";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { BatchWorkspace } from "@/components/grooming/batch-workspace";
import { Chip } from "@/components/grooming/grooming-chips";
import { AgendaPresent, PresentButton, usePresentParam, usePresentShortcut } from "@/components/grooming/present-mode";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import { SessionBuild, agendaCounts, agendaEntries } from "@/components/grooming/session-build";
import { SessionPrepared, preparedStatus } from "@/components/grooming/session-prepared";
import { useBatchRun } from "@/components/grooming/use-batch-run";
import { PersonaPicker, usePersonaChoice } from "@/components/toolbox/persona-picker";
import { useRegisterCommands, type GroomingCommand } from "@/components/grooming/use-command-registry";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  groomingKeys,
  useGroomingBacklog,
  useGroomingBatch,
  useGroomingBatches,
  useCreateDatedAgenda,
  useProjectSession,
  useSetSessionDate,
} from "@/hooks/queries/use-grooming";
import { refreshCachedBacklog } from "@/hooks/queries/use-brief-staleness";
import { useMe } from "@/hooks/queries/use-me";
import { canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { usePageTitle } from "@/hooks/use-page-title";
import type {
  GroomingBacklogItem,
  GroomingBatch,
  GroomingMeetingPattern,
} from "@/lib/api/grooming";
import { getAdoPatErrorCode } from "@/lib/api/work-items";
import { agendaErrorMessage, groomingApiErrorCode } from "@/lib/grooming/error-messages";
import {
  currentAgendaRun,
  dateParts,
  isEarlierAgenda,
  isLaterAgenda,
  laterAgendaRun,
  localToday,
  runMeetingDate,
} from "@/lib/grooming/agenda-run";
import {
  formatMeetingDate,
  isMeetingDate,
  isPastMeeting,
  localDateIn,
  meetingAfter,
  prepareByDate,
  prepareByText,
  prepareByTone,
} from "@/lib/grooming/meeting-dates";
import { formatLocalClockParts } from "@/lib/grooming/local-time";
import { groomingPath, projectPath } from "@/lib/grooming/paths";
import { useRunnerBridge } from "@/lib/grooming/runner-bridge";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { detectedTimeZone, isKnownTimeZone } from "@/lib/time-zones";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Session screen (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1402; Claude
 * Design "SpecTree Grooming Desktop", Session). It replaces E279's Session
 * prep.
 *
 * `/grooming/:project/session` is the **build step**: the next session's date
 * (the project's meeting pattern, overridable for one session), the Ready to
 * groom queue beside the project's one shared agenda, Share agenda, Present,
 * and "Prepare N briefs" (a batch run in SpecTree Toolbox for the agenda items
 * without a current brief). What shows follows the caller's Toolbox
 * capabilities on the project (epic PI-SPEC-SW-E283): `manage-agenda` builds
 * the agenda and changes its date, `prepare-sessions` prepares briefs,
 * `map-project` links to Project → Meeting schedule (the meeting pattern) and
 * `present` opens Present mode.
 * Everyone else (Viewer, Developer, QA Tester) sees the agenda read-only.
 *
 * `/grooming/:project/session/:batchId` is the **run step**: `BatchWorkspace`
 * (feature PI-SPEC-SW-1403) draws its header, the Workspace | Agenda switch
 * and both views; this page adds the back arrow to the build step.
 *
 * Preparing an agenda turns the shared draft into a run (PKG-PM-C, frames C3,
 * C4 and 1e): Prepare starts it in place and the build route then shows the
 * run's progress (`SessionPrepared`) while it belongs to the coming session,
 * with "Start the next meeting's agenda" for the meeting after. The header
 * names the meeting and when to aim to prepare it (a hint, never blocking);
 * before a schedule exists it offers "Set the meeting schedule". The
 * schedule is set on Project → Meeting schedule (PKG-PM-F, frame F1), which
 * replaced the "When does the grooming meeting repeat?" dialog; with a start
 * time the header adds the meeting's hour in the viewer's own zone.
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) "Prepare briefs" adds the
 * batch to the app's run queue (through `BatchWorkspace`), and both steps
 * re-read the session and the run whenever the queue reports one of this
 * project's entries settled, so a brief shows up without a reload.
 *
 * E323 (ER-1710, feature PI-SPEC-SW-1739): an agenda stays editable until
 * its meeting has passed, prepared or not. Change date works on a prepared
 * run too (409 GROOMING_SESSION_DATE_TAKEN when another agenda holds the
 * date), and a past meeting's agenda is a record with no edit controls.
 * Several drafts may exist (a Move starts one for any upcoming meeting):
 * Session still opens on the coming meeting, and `?agenda=next` shows the
 * meeting after it, even when the coming meeting's agenda is a draft and a
 * later one was prepared early (`isEarlierAgenda`).
 */

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** `2026-10-15` → "Thursday, Oct 15". */
export function formatSessionDate(date: string): string {
  return dateParts(date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

/** `2026-10-15` → "Thursday". */
export function sessionWeekday(date: string): string {
  return dateParts(date).toLocaleDateString("en-US", { weekday: "long" });
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

/** "Every Thursday" / "Every 2 weeks on Thursday". */
export function describePattern(pattern: GroomingMeetingPattern): string {
  const day = WEEKDAYS[pattern.weekday] ?? "the same day";
  return pattern.everyNWeeks === 1 ? `Every ${day}` : `Every ${String(pattern.everyNWeeks)} weeks on ${day}`;
}

// ---------------------------------------------------------------------------
// Following the run queue (E284)
// ---------------------------------------------------------------------------

/**
 * A signature of the project's finished queue entries (of one batch, when
 * `batchId` is given): it changes whenever one of them settles. Null while
 * the queue has not loaded. Pure; exported for tests.
 */
export function settledSignature(state: GroomingQueueState | null, project: string, batchId: string | null): string | null {
  if (!state) return null;
  const projectKey = project.trim().toLowerCase();
  return state.completed
    .filter((entry) => entry.project.trim().toLowerCase() === projectKey && (batchId === null || entry.batchId === batchId))
    .map((entry) => `${entry.id}:${entry.state}`)
    .join("|");
}

/**
 * Re-read the session (and the run, given its id) when the run queue reports
 * that one of this project's entries settled. Uses the existing query keys;
 * nothing happens in a browser or with a contract-2 app. On the run page the
 * cached backlog is only marked out of date (`refreshCachedBacklog`): a new
 * brief replaces its latest one, and only a page that shows the backlog
 * reads it again.
 */
export function useQueueSettledRefresh(project: string, batchId: string | null): void {
  const { state } = useGroomingQueue();
  const queryClient = useQueryClient();
  const signature = settledSignature(state, project, batchId);
  const previous = useRef<string | null>(null);
  useEffect(() => {
    if (signature === null) return;
    if (previous.current !== null && previous.current !== signature) {
      if (batchId) {
        void queryClient.invalidateQueries({ queryKey: groomingKeys.session(project) });
        void queryClient.invalidateQueries({ queryKey: groomingKeys.batches(project) });
        void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(batchId), exact: true });
        refreshCachedBacklog(queryClient, project);
      } else {
        // The build step also shows each item's brief state from the backlog read.
        void queryClient.invalidateQueries({ queryKey: groomingKeys.project(project) });
      }
    }
    previous.current = signature;
  }, [signature, project, batchId, queryClient]);
}

// ---------------------------------------------------------------------------
// Which agenda the build step shows
// ---------------------------------------------------------------------------

// Which run is current (`latestAgendaRun`, `isCurrentRun`, `readyToPostRun`)
// lives in `@/lib/grooming/agenda-run`, shared with the icon rail's
// ready-to-post badge (UX-PM-32).

// ---------------------------------------------------------------------------
// Share agenda
// ---------------------------------------------------------------------------

/** The read-only agenda page's address for the Teams meeting chat. */
export function agendaShareUrl(project: string, batchId: string): string {
  return `${window.location.origin}${groomingPath(project, `agenda/${batchId}`)}`;
}

async function shareAgenda(project: string, batchId: string): Promise<void> {
  const url = agendaShareUrl(project, batchId);
  try {
    await navigator.clipboard.writeText(url);
    showGroomingToast("Agenda link copied. Paste it into the Teams meeting chat.");
  } catch {
    showGroomingToast(`The link could not be copied. Copy it from here: ${url}`, "info");
  }
}

// ---------------------------------------------------------------------------
// Header controls
// ---------------------------------------------------------------------------

const inputClassName =
  "h-8 rounded-lg border border-[#27272A] bg-[#111113] px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50";

/**
 * The words for a failed Change date (E323): the contract-4 agenda codes
 * (GROOMING_SESSION_DATE_TAKEN: move the items to that meeting instead;
 * GROOMING_AGENDA_LOCKED), a past date, else a plain retry. Pure; exported
 * for tests.
 */
export function changeDateError(cause: unknown): string {
  const agenda = agendaErrorMessage(cause);
  if (agenda) return agenda;
  switch (groomingApiErrorCode(cause)) {
    case "VALIDATION_ERROR":
      return "Pick today or a later date.";
    case "GROOMING_BATCH_NOT_DRAFT":
      return "A prepared agenda keeps a date. Pick the meeting's date instead.";
    default:
      return "The date could not be saved. Try again in a moment.";
  }
}

/**
 * "Change date": this session's date, or back to the meeting pattern. Needs
 * an agenda: the shared draft, or (E323) a prepared run before its meeting.
 */
function ChangeDate({
  project,
  draft,
  sessionDate,
  patternDate,
  resetTo = null,
}: {
  project: string;
  /** The agenda whose date changes: the shared draft or a prepared run; null before the agenda has an item. */
  draft: GroomingBatch | null;
  sessionDate: string | null;
  /** The date the meeting pattern gives this agenda (the next meeting, or the one after for the next meeting's agenda). */
  patternDate: string | null;
  /** What Reset saves: null (follow the pattern), or the pattern's date for the next meeting's agenda. */
  resetTo?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const setDate = useSetSessionDate(project);
  const overridden = Boolean(draft?.sessionDate) && draft?.sessionDate !== resetTo;

  const save = (next: string | null) => {
    if (!draft) return;
    setError(null);
    setDate.mutate(
      { batchId: draft.id, sessionDate: next },
      {
        onSuccess: () => {
          setOpen(false);
          showGroomingToast(next ? `Session moved to ${formatSessionDate(next)}` : "Session date follows the meeting pattern");
        },
        onError: (cause) => {
          setError(changeDateError(cause));
        },
      }
    );
  };

  return (
    <div className="relative inline-flex">
      <WhyDisabled reason={draft ? null : ADD_AN_ITEM_FIRST} testId="change-date-why">
        <button
          type="button"
          className="inline-flex h-6 items-center rounded px-0.5 text-[#6CC4FF] hover:underline disabled:pointer-events-none disabled:opacity-50"
          aria-expanded={open}
          disabled={!draft}
          title={draft ? undefined : ADD_AN_ITEM_FIRST}
          onClick={() => {
            setValue(sessionDate ?? localToday());
            setError(null);
            setOpen(!open);
          }}
        >
          Change date
        </button>
      </WhyDisabled>
      {open && draft && (
        <div
          className="absolute left-0 top-9 z-20 flex w-[280px] flex-col gap-3 rounded-[10px] border border-[#27272A] bg-[#141417] p-4 shadow-[0_16px_40px_rgba(0,0,0,.5)]"
          role="dialog"
          aria-label="Change the session date"
          data-testid="change-date"
        >
          <label className="flex flex-col gap-1.5 text-[13px] text-muted-foreground">
            Date of this session
            <input
              type="date"
              className={inputClassName}
              aria-label="Session date"
              min={localToday()}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
          </label>
          <p className={cn("text-[13px]", TOOLBOX_TEXT.faint)}>
            {patternDate
              ? `The meeting pattern says ${formatSessionDate(patternDate)}. A changed date applies to this session only.`
              : "No meeting pattern is set, so this session keeps the date you pick."}
          </p>
          {error && (
            <p className="text-[13px] text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="flex items-center gap-2">
            {overridden && patternDate && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={setDate.isPending}
                onClick={() => {
                  save(resetTo);
                }}
              >
                Reset to the meeting pattern
              </Button>
            )}
            <span className="flex-1" />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={!/^\d{4}-\d{2}-\d{2}$/.test(value) || setDate.isPending}
              onClick={() => {
                save(value);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The meeting schedule's place (PKG-PM-F): Project → Meeting schedule. */
export function meetingSchedulePath(project: string): string {
  return projectPath(project, "meeting-schedule");
}

/**
 * The hour a meeting starts, "10:00 AM", in `timeZone` (the viewer's own).
 * When that hour falls on another day for the viewer than `meetingDate` (the
 * pattern's date, in the meeting's zone), it names the viewer's day:
 * "7:30 AM on Tuesday, Oct 27 your time". Null for a missing or unreadable
 * start. Pure; exported for tests.
 */
export function meetingStartHour(
  start: string | null | undefined,
  timeZone: string,
  meetingDate: string | null = null
): string | null {
  if (!start) return null;
  const time = formatLocalClockParts(start, { timeZone })?.time ?? null;
  if (!time || !meetingDate) return time;
  const viewerDate = localDateIn(timeZone, new Date(start));
  return viewerDate === meetingDate ? time : `${time} on ${formatSessionDate(viewerDate)} your time`;
}

/** The viewer's zone: their profile's (`/me`) when the browser knows it, else this computer's. */
function useViewerTimeZone(): string {
  const { data: me } = useMe();
  const zone = me?.timeZone;
  return zone && isKnownTimeZone(zone) ? zone : detectedTimeZone();
}

/** Why Share agenda, Present and Change date are greyed before the agenda has an item (frame 1e). */
export const ADD_AN_ITEM_FIRST = "Add an item to the agenda first";

/**
 * Says on hover and keyboard focus why the wrapped control is disabled
 * (UX-PM-29). A disabled button fires no pointer events, so the reason sits
 * on a focusable wrapper. With no reason the control is returned as is.
 */
export function WhyDisabled({ reason, testId, children }: { reason: string | null; testId?: string; children: ReactElement }) {
  if (!reason) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex" tabIndex={0} aria-label={reason} data-testid={testId}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="border border-[#27272A] bg-[#18181B] text-foreground">{reason}</TooltipContent>
    </Tooltip>
  );
}

/** The Present button when the page passes none (Present stays unavailable). */
function PresentPlaceholder() {
  return (
    <WhyDisabled reason={ADD_AN_ITEM_FIRST} testId="present-why">
      <PresentButton onPresent={() => undefined} disabled />
    </WhyDisabled>
  );
}

/** The header's "No date yet · 3 on the agenda" (frame 1e). Pure; exported for tests. */
export function noDateText(items: number): string {
  return `No date yet · ${String(items)} on the agenda`;
}

/** The prepare-by hint (frame C3): grey before the business day before the meeting, amber from then on; never blocking. */
function PrepareBy({ sessionDate, today }: { sessionDate: string; today: string }) {
  const tone = prepareByTone(today, prepareByDate(sessionDate));
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", tone === "due" ? "text-[#FDE68A]" : "text-muted-foreground")}
      data-testid="prepare-by"
      data-tone={tone}
    >
      <Clock className="h-3.5 w-3.5" aria-hidden="true" />
      {prepareByText(sessionDate)}
    </span>
  );
}

/** "Preparing since Fri 2:10 PM" and the like, in place of the prepare-by hint once the agenda is prepared (frame C4). */
function PreparedStatus({ run }: { run: GroomingBatch }) {
  const status = preparedStatus(run);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5",
        status.tone === "stopped" ? "text-muted-foreground" : "text-[#4ADE80]"
      )}
      data-testid="prepared-status"
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
      {status.text}
    </span>
  );
}

function Dot() {
  return (
    <span className={TOOLBOX_TEXT.faint} aria-hidden="true">
      ·
    </span>
  );
}

export interface SessionHeaderProps {
  project: string;
  /** `YYYY-MM-DD`, or null when neither a pattern nor an override gives one. */
  sessionDate: string | null;
  nextMeetingDate: string | null;
  /**
   * The date of the meeting the prepared run is for, when this page shows the
   * draft agenda of the meeting after it (`?agenda=next`, PKG-PM-C Mac walk
   * 2026-10-23): the header then says so and links back. Null otherwise.
   */
  runMeetingDate?: string | null;
  /** The next meeting's start (the pattern's start time), an ISO instant; null without a start time (PKG-PM-F). */
  nextMeetingStart?: string | null;
  /** The viewer's IANA zone, for the hour. Default: this computer's. */
  viewerTimeZone?: string;
  meetingPattern: GroomingMeetingPattern | null;
  /** The shared draft agenda (the date can change only on it). */
  draft: GroomingBatch | null;
  /** The agenda shown, for Share agenda. */
  agendaId: string | null;
  counts: { items: number; briefed: number; needBrief: number };
  /** `prepare-sessions` on the project: Prepare briefs. */
  canPrepareSessions: boolean;
  /** `manage-agenda` on the project: change the session date. */
  canManageAgenda: boolean;
  /** Project → Meeting schedule (`map-project`: Project Managers and Admins); undefined without it. */
  scheduleHref?: string | undefined;
  /** Leave out the line's "Set the meeting schedule" (the first-run card offers it). */
  hideScheduleLink?: boolean;
  /** A prepared run of this session's agenda, for Open the briefs. */
  runId: string | null;
  /**
   * The prepared run shown on the page (frame C4): its status replaces the
   * prepare-by hint and Prepare goes; Change date moves the run's own date
   * (E323).
   */
  preparedRun?: GroomingBatch | null;
  /** The shown agenda's meeting has passed (E323): no Change date. */
  locked?: boolean;
  /**
   * The next meeting's agenda when Session shows a draft for the coming
   * meeting and a later meeting was prepared early (E323): a link to it. Null
   * otherwise (the prepared screen's own card links there).
   */
  laterMeeting?: { date: string | null; href: string } | null;
  /** Present mode's button (feature PI-SPEC-SW-1404); a disabled one when omitted, none when null. */
  presentSlot?: ReactNode;
  onPrepare: () => void;
  /** False in a browser or an older app: Prepare stays disabled and the runner fallback shows. */
  canPrepare: boolean;
  /** Today, `YYYY-MM-DD` in the browser's time zone (the prepare-by tone). */
  today?: string;
}

/**
 * The Session header (frames C3, C4, 1e, F1): "Next session" over the
 * meeting's date, with its hour in the viewer's own zone when the pattern has
 * a start time and the date is the pattern's (a changed date shows the date
 * only), then one line: how the meeting repeats (a link to Project → Meeting
 * schedule for `map-project`) · "Aim to prepare by …" (or the run's status
 * once prepared) · Change date. Without a date: "Next session" and "No date yet · N on the
 * agenda". Then Share agenda, Present, Open the briefs and Prepare N briefs.
 */
export function SessionHeader({
  project,
  sessionDate,
  nextMeetingDate,
  nextMeetingStart = null,
  viewerTimeZone = detectedTimeZone(),
  meetingPattern,
  draft,
  agendaId,
  counts,
  canPrepareSessions,
  canManageAgenda,
  scheduleHref,
  hideScheduleLink = false,
  runId,
  preparedRun = null,
  presentSlot,
  onPrepare,
  canPrepare,
  today = localToday(),
  runMeetingDate = null,
  locked = false,
  laterMeeting = null,
}: SessionHeaderProps) {
  // E323: a prepared run's date changes too, until its meeting has passed.
  const dated = preparedRun ?? draft;
  // A date the schedule gives (the next meeting or a later one) is no change; another day is (PKG-PM-C Mac walk).
  const overridden =
    Boolean(dated?.sessionDate) && meetingPattern !== null && !isMeetingDate(meetingPattern, dated?.sessionDate ?? "");
  const patternDate = runMeetingDate && meetingPattern ? meetingAfter(meetingPattern, runMeetingDate) : nextMeetingDate;
  // The hour belongs to the pattern's meeting: a date changed for this session shows the date only.
  const hour = sessionDate && sessionDate === nextMeetingDate ? meetingStartHour(nextMeetingStart, viewerTimeZone, nextMeetingDate) : null;
  const showPrepare = canPrepareSessions && draft !== null && preparedRun === null;
  // An empty agenda has nothing to prepare yet: say what to do, like Share agenda and Present.
  const prepareLabel =
    counts.items === 0
      ? "Prepare briefs"
      : counts.needBrief === 0
        ? "Every item has a brief"
        : `Prepare ${String(counts.needBrief)} ${counts.needBrief === 1 ? "brief" : "briefs"}`;
  const shareReason = agendaId && counts.items > 0 ? null : ADD_AN_ITEM_FIRST;

  const parts: ReactNode[] = [];
  if (!sessionDate) {
    parts.push(
      <span key="no-date" className="text-muted-foreground" data-testid="session-counts">
        {noDateText(counts.items)}
      </span>
    );
  }
  if (meetingPattern) {
    parts.push(
      scheduleHref ? (
        <Link
          key="pattern"
          to={scheduleHref}
          className="text-muted-foreground hover:text-foreground hover:underline"
          title="Meeting schedule"
          data-testid="meeting-pattern"
        >
          {describePattern(meetingPattern)}
        </Link>
      ) : (
        <span key="pattern" className="text-muted-foreground" data-testid="meeting-pattern">
          {describePattern(meetingPattern)}
        </span>
      )
    );
  } else if (scheduleHref && !hideScheduleLink) {
    parts.push(
      <Link key="pattern" to={scheduleHref} className="text-[#6CC4FF] hover:underline" data-testid="meeting-schedule-link">
        Set the meeting schedule
      </Link>
    );
  }
  if (preparedRun) parts.push(<PreparedStatus key="status" run={preparedRun} />);
  else if (sessionDate) parts.push(<PrepareBy key="prepare-by" sessionDate={sessionDate} today={today} />);
  if (canManageAgenda && !locked) {
    parts.push(
      <ChangeDate
        key="change"
        project={project}
        draft={dated}
        sessionDate={sessionDate}
        patternDate={patternDate}
        // A prepared run keeps a date (clearing it is draft-only), so its Reset saves the pattern's date.
        resetTo={runMeetingDate || preparedRun ? patternDate : null}
      />
    );
  }
  if (laterMeeting) {
    parts.push(
      <Link key="later" to={laterMeeting.href} className="text-[#6CC4FF] hover:underline" data-testid="open-later-agenda">
        Next: {laterMeeting.date ? formatMeetingDate(laterMeeting.date) : "the meeting after"}
      </Link>
    );
  }
  if (runMeetingDate) {
    parts.push(
      <Link key="back" to={groomingPath(project, "session")} className="text-[#6CC4FF] hover:underline" data-testid="back-to-run">
        Back to {formatMeetingDate(runMeetingDate)}
      </Link>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-3 pb-3.5" data-testid="session-header">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={TOOLBOX_TEXT.eyebrow}>
          {runMeetingDate ? "The meeting after this one" : sessionDate ? "Next session" : "Grooming"}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className={TOOLBOX_PAGE_TITLE} data-testid="session-date">
            {sessionDate ? formatSessionDate(sessionDate) : "Next session"}
            {hour && <span data-testid="session-hour">{` · ${hour}`}</span>}
          </h1>
          {overridden && (
            <Chip tone="warn" testId="date-overridden">
              Changed for this session
            </Chip>
          )}
        </div>
        <div className={cn("flex flex-wrap items-center gap-2", TOOLBOX_TEXT.content)} data-testid="session-line">
          {parts.flatMap((part, index) => (index === 0 ? [part] : [<Dot key={`dot-${String(index)}`} />, part]))}
        </div>
      </div>
      <WhyDisabled reason={shareReason} testId="share-why">
        <Button
          type="button"
          variant="outline"
          disabled={shareReason !== null}
          title={shareReason ?? undefined}
          onClick={() => {
            if (agendaId) void shareAgenda(project, agendaId);
          }}
        >
          <LinkIcon aria-hidden="true" />
          Share agenda
        </Button>
      </WhyDisabled>
      {presentSlot === undefined ? <PresentPlaceholder /> : presentSlot}
      {runId && (
        <Button asChild variant="outline">
          <Link to={groomingPath(project, `session/${runId}`)}>
            <ArrowRight aria-hidden="true" />
            Open the briefs
          </Link>
        </Button>
      )}
      {showPrepare && (
        <Button
          type="button"
          className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={!canPrepare || counts.needBrief === 0}
          title={counts.items === 0 ? ADD_AN_ITEM_FIRST : undefined}
          onClick={onPrepare}
          data-testid="prepare-briefs"
        >
          <Sparkles aria-hidden="true" />
          {prepareLabel}
        </Button>
      )}
    </div>
  );
}

/** "Or pick one date" (frame 1e, `manage-agenda`): date the shared agenda without a meeting pattern. */
function PickOneDate({ project, scheduleHref }: { project: string; scheduleHref: string | undefined }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useCreateDatedAgenda(project);

  const save = () => {
    setError(null);
    create.mutate(value, {
      onSuccess: () => {
        setOpen(false);
        showGroomingToast(`Next session set for ${formatSessionDate(value)}`);
      },
      onError: () => {
        setError("The date could not be saved. Try again in a moment.");
      },
    });
  };

  return (
    <div className="relative flex justify-center">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={open}
        onClick={() => {
          setValue(localToday());
          setError(null);
          setOpen(!open);
        }}
      >
        Or pick one date
      </Button>
      {open && (
        <div
          className="absolute right-0 top-10 z-20 flex w-[280px] flex-col gap-3 rounded-[10px] border border-[#27272A] bg-[#141417] p-4 shadow-[0_16px_40px_rgba(0,0,0,.5)]"
          role="dialog"
          aria-label="Pick the date of the next session"
          data-testid="pick-one-date"
        >
          <label className="flex flex-col gap-1.5 text-[13px] text-muted-foreground">
            Date of the next session
            <input
              type="date"
              className={inputClassName}
              aria-label="Session date"
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
          </label>
          <p className={cn("text-[13px]", TOOLBOX_TEXT.faint)}>
            This session only.{" "}
            {scheduleHref ? (
              <Link to={scheduleHref} className="text-[#6CC4FF] hover:underline">
                Set the meeting schedule
              </Link>
            ) : (
              "Set the meeting schedule"
            )}{" "}
            to have the date fill in by itself.
          </p>
          {error && (
            <p className="text-[13px] text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="border-primary bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={!/^\d{4}-\d{2}-\d{2}$/.test(value) || create.isPending}
              onClick={save}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Session before a schedule exists (frame 1e, UX-PM-29): one clear next step
 * instead of a "no date" headline. Map-project holders get **Set the meeting
 * schedule** (a link to Project → Meeting schedule, PKG-PM-F frame F1),
 * manage-agenda holders also "Or pick one date"; everyone else
 * reads who sets it. No agenda-size hint (01-roles.md §9.6).
 */
function FirstRunCard({
  project,
  scheduleHref,
  canManageAgenda,
}: {
  project: string;
  scheduleHref: string | undefined;
  canManageAgenda: boolean;
}) {
  return (
    <section
      className="mb-3.5 flex flex-wrap items-center gap-5 rounded-[10px] border border-[#3F1D24] bg-[#140C0E] px-[22px] py-5"
      aria-labelledby="first-run-heading"
      data-testid="session-first-run"
    >
      <CalendarClock className="h-[30px] w-[30px] flex-none text-[#FF6B81]" aria-hidden="true" />
      <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
        <h2 id="first-run-heading" className="font-display text-base font-semibold">
          Set when your grooming meeting repeats
        </h2>
        <p className={cn(TOOLBOX_TEXT.content, "leading-relaxed text-muted-foreground")}>
          Pick the day and how often. The date of the next session then fills in by itself, and the agenda and the briefs
          are prepared for it. You can still move one session to another day.
        </p>
      </div>
      {scheduleHref ? (
        <div className="flex flex-none flex-col gap-2">
          <Button asChild className="border-primary bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to={scheduleHref} data-testid="set-meeting-schedule">
              <Repeat aria-hidden="true" />
              Set the meeting schedule
            </Link>
          </Button>
          {canManageAgenda && <PickOneDate project={project} scheduleHref={scheduleHref} />}
        </div>
      ) : (
        <div className="flex flex-none flex-col gap-2">
          <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="first-run-who">
            A Project Manager sets when the meeting repeats.
          </p>
          {canManageAgenda && <PickOneDate project={project} scheduleHref={undefined} />}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Present mode (feature PI-SPEC-SW-1404)
// ---------------------------------------------------------------------------

/**
 * Present mode's entry points on the Session screen, both steps: its open
 * state in the URL (`?present=1` opens it from a link), the P key and the
 * palette's "Present the agenda". Only with the `present` capability on the
 * project (Admin, Project Manager, Product Owner; E283): Present mode has no
 * API action of its own, so `GET /toolbox/access` is its gate.
 */
function useSessionPresent(canPresent: boolean): { presenting: boolean; openPresent: () => void; closePresent: () => void } {
  const { open, openPresent, closePresent } = usePresentParam();
  usePresentShortcut(canPresent && !open, openPresent);
  const commands = useMemo(
    (): GroomingCommand[] =>
      canPresent
        ? [
            {
              id: "present",
              label: "Present the agenda",
              hint: "P",
              icon: Presentation,
              keywords: "present slides meeting screen share teams",
              run: openPresent,
            },
          ]
        : [],
    [canPresent, openPresent]
  );
  useRegisterCommands("present", commands);
  return { presenting: open && canPresent, openPresent, closePresent };
}

// ---------------------------------------------------------------------------
// The two steps
// ---------------------------------------------------------------------------

function Loading({ label }: { label: string }) {
  return (
    <div className="flex min-h-[240px] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

/**
 * `/grooming/:project/session`: build the next session's agenda (frames C3
 * and 1e), and once it is prepared follow its run on the same screen (frame
 * C4, PKG-PM-C). Prepare starts the run in place (`useBatchRun`); the page
 * keeps the started agenda as "prepared" until the session read shows the run
 * or a new draft agenda (Start the next meeting's agenda) takes the page over.
 */
function SessionBuildStep({ project }: { project: string }) {
  const { data: access } = useToolboxAccess();
  const canManageAgenda = canOn(access, project, "manage-agenda");
  const canPrepareSessions = canOn(access, project, "prepare-sessions");
  const canSetPattern = canOn(access, project, "map-project");
  const canPresent = canOn(access, project, "present");
  // E283: "Working as" before Prepare briefs when the person's roles carry more than one persona.
  const persona = usePersonaChoice(canPrepareSessions ? project : null);
  const { runner, support } = useRunnerBridge();
  const session = useProjectSession(project);
  const batches = useGroomingBatches(project);
  // One ADO-order read (shared with Present mode and brief staleness); the queue's sort is applied client-side (E303).
  const backlog = useGroomingBacklog(project, "all", "rank");
  useQueueSettledRefresh(project, null);
  const viewerTimeZone = useViewerTimeZone();
  /** The draft this page just started (Prepare): shown as prepared until the batch list shows it as a run. */
  const [startedId, setStartedId] = useState<string | null>(null);
  /** Whether that draft was the next meeting's agenda (`?agenda=next`): it shows only there, never instead of the coming meeting. */
  const [startedLater, setStartedLater] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionDraft = session.data?.batch ?? null;
  const today = localToday();
  const currentRun = currentAgendaRun(batches.data, session.data, today);
  // The next meeting's agenda, a draft (Start the next meeting's agenda) or a run prepared early, waits behind the
  // current run until the run's meeting has passed; `?agenda=next` opens it (PKG-PM-C Mac walk, 2026-10-23).
  const laterRun = laterAgendaRun(batches.data, session.data, today, currentRun);
  // Session shows two meetings: the coming one and the next. A draft for a meeting after the next one's prepared run
  // (made before that run was prepared) waits until the coming meeting has passed (Copilot review of #545).
  const draftDate = session.data?.sessionDate ?? null;
  const laterRunDate = laterRun && session.data ? runMeetingDate(laterRun, session.data) : null;
  const draftBeyondNext = draftDate !== null && laterRunDate !== null && draftDate > laterRunDate;
  const laterDraft = Boolean(sessionDraft && currentRun && session.data && isLaterAgenda(session.data, currentRun)) && !draftBeyondNext;
  // E323: several drafts may exist (a Move starts one for any upcoming meeting). The shared draft is the earliest; when
  // it is for an EARLIER meeting than the current run (a run prepared early for a later meeting), the draft's meeting
  // is the coming one: Session opens on the draft and `?agenda=next` shows the run.
  const draftFirst =
    Boolean(sessionDraft && currentRun && session.data && isEarlierAgenda(session.data, currentRun)) && sessionDraft?.id !== startedId;
  const hasNextAgenda = draftFirst || laterDraft || laterRun !== undefined || (startedId !== null && startedLater);
  const showNextAgenda = hasNextAgenda && searchParams.get("agenda") === "next";
  // A draft other than the one just started is the agenda being built.
  const draft =
    sessionDraft &&
    sessionDraft.id !== startedId &&
    !draftBeyondNext &&
    (!laterDraft || showNextAgenda) &&
    !(draftFirst && showNextAgenda)
      ? sessionDraft
      : null;
  const nextAgendaPath = `${groomingPath(project, "session")}?agenda=next`;
  const shownRunDate =
    showNextAgenda && session.data
      ? draftFirst
        ? draftDate
        : currentRun
          ? runMeetingDate(currentRun, session.data)
          : null
      : null;
  // The run just started shows only in the view it was started from: Back to the coming meeting leaves it.
  const startedHere = startedId !== null && startedLater === showNextAgenda ? startedId : null;
  // Without a draft, the current run (the one the icon rail's ready-to-post badge counts, UX-PM-32) is shown
  // prepared, or on `?agenda=next` the next meeting's run (the current run itself when a draft comes first).
  const nextRunId = draftFirst ? currentRun?.id : laterRun?.id;
  const preparedId = draft ? null : (startedHere ?? (showNextAgenda ? nextRunId : draftFirst ? undefined : currentRun?.id) ?? null);
  const preparedRead = useGroomingBatch(preparedId ?? undefined, { poll: runner === null });
  const preparedRun =
    preparedId === null
      ? null
      : (preparedRead.data ?? (sessionDraft && sessionDraft.id === preparedId ? sessionDraft : null));
  const agenda = draft ?? preparedRun;
  const batchRun = useBatchRun(project, agenda);
  // A start the app accepted can still come to nothing (its queue entry removed, or the engine's
  // POST /start refused): once the run was seen in the queue and is gone while SpecTree still has
  // a draft, show the draft again.
  const startedSeen = useRef<string | null>(null);
  const startedStillDraft = startedId !== null && preparedRead.data?.id === startedId && preparedRead.data.status === "draft";
  // Once the batch list shows the started draft as a run, the list's own reading takes over.
  const startedListed = startedId !== null && (batches.data ?? []).some((b) => b.id === startedId && b.status !== "draft");
  useEffect(() => {
    if (startedListed) setStartedId(null);
  }, [startedListed]);
  useEffect(() => {
    if (startedId === null) {
      startedSeen.current = null;
      return;
    }
    if (batchRun.active) {
      startedSeen.current = startedId;
    } else if (startedSeen.current === startedId && !batchRun.busy && startedStillDraft) {
      setStartedId(null);
    }
  }, [startedId, batchRun.active, batchRun.busy, startedStillDraft]);

  const backlogById = useMemo(
    () => new Map<string, GroomingBacklogItem>((backlog.data?.items ?? []).map((item) => [item.itemId, item])),
    [backlog.data]
  );
  const entries = useMemo(() => agendaEntries(agenda, backlogById), [agenda, backlogById]);
  const counts = agendaCounts(entries);

  const sessionDate = draft
    ? (session.data?.sessionDate ?? null)
    : preparedRun
      ? (preparedRun.sessionDate ?? session.data?.nextMeetingDate ?? null)
      : (session.data?.sessionDate ?? null);
  const sessionWords = sessionDate ? `${sessionWeekday(sessionDate)}'s session` : "the next session";
  const { presenting, openPresent, closePresent } = useSessionPresent(canPresent && agenda !== null && entries.length > 0);
  const meetingPattern = session.data?.meetingPattern ?? null;
  // E323: once the shown agenda's meeting has passed (in the pattern's time zone) it is a record. Only an agenda's own
  // date can pass while Session shows it: an undated draft is for the next meeting, and an undated run shown here was
  // prepared for the coming one (isCurrentRun).
  const locked = isPastMeeting(agenda?.sessionDate ?? null, meetingPattern);
  const scheduleHref = canSetPattern ? meetingSchedulePath(project) : undefined;
  const firstRun = meetingPattern === null && !sessionDate && entries.length === 0 && preparedRun === null;

  if (session.isLoading) return <Loading label="Loading the next session…" />;
  if (session.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        The next session could not be loaded. Try again in a moment.
      </p>
    );
  }

  const prepare = () => {
    if (!draft || !runner) return;
    const id = draft.id;
    // The persona is saved first; SpecTree stamps it on the batch. Then the run starts here, in SpecTree Toolbox
    // (the app's engine starts the batch, POST /start), and this page follows it (frame C4).
    void persona.ensureSaved(project).then(
      () => {
        setStartedId(id);
        setStartedLater(showNextAgenda);
        void batchRun.start().then((ok) => {
          if (!ok) setStartedId(null);
        });
      },
      () => undefined
    );
  };

  const presentDisabled = agenda === null || entries.length === 0;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-0 flex-1 flex-col" data-testid="session-screen">
        <SessionHeader
          project={project}
          sessionDate={sessionDate}
          nextMeetingDate={session.data?.nextMeetingDate ?? null}
          nextMeetingStart={session.data?.nextMeetingStart ?? null}
          viewerTimeZone={viewerTimeZone}
          meetingPattern={meetingPattern}
          draft={draft}
          agendaId={agenda?.id ?? null}
          counts={counts}
          canPrepareSessions={canPrepareSessions}
          canManageAgenda={canManageAgenda}
          scheduleHref={scheduleHref}
          hideScheduleLink={firstRun}
          runId={preparedRun?.id ?? (showNextAgenda || draftFirst ? null : (currentRun?.id ?? null))}
          preparedRun={preparedRun}
          runMeetingDate={shownRunDate}
          locked={locked}
          laterMeeting={
            draftFirst && !showNextAgenda && currentRun && session.data
              ? { date: runMeetingDate(currentRun, session.data), href: nextAgendaPath }
              : null
          }
          presentSlot={
            canPresent ? (
              <WhyDisabled reason={presentDisabled ? ADD_AN_ITEM_FIRST : null} testId="present-why">
                <PresentButton onPresent={openPresent} disabled={presentDisabled} />
              </WhyDisabled>
            ) : null
          }
          onPrepare={prepare}
          canPrepare={runner !== null}
          today={today}
        />
        {presenting && <AgendaPresent project={project} batch={agenda} sessionDate={sessionDate} onClose={closePresent} />}
        {preparedRun ? (
          <SessionPrepared
            project={project}
            run={preparedRun}
            sessionDate={sessionDate}
            meetingPattern={meetingPattern}
            typicalItemSeconds={session.data?.typicalItemSeconds ?? null}
            batchRun={batchRun}
            canPrepare={canPrepareSessions}
            canManageAgenda={canManageAgenda}
            // The next meeting's own page offers no meeting after it: Session holds two meetings, never a third.
            showMeetingAfter={!showNextAgenda}
            nextAgenda={
              showNextAgenda
                ? null
                : laterDraft && sessionDraft
                  ? { date: session.data?.sessionDate ?? null, items: sessionDraft.items.length, href: nextAgendaPath, prepared: false }
                  : laterRun && session.data
                    ? { date: runMeetingDate(laterRun, session.data), items: laterRun.itemCount, href: nextAgendaPath, prepared: true }
                    : null
            }
            onNextAgendaStarted={() => {
              void navigate(nextAgendaPath);
            }}
          />
        ) : (
          <>
            {firstRun && <FirstRunCard project={project} scheduleHref={scheduleHref} canManageAgenda={canManageAgenda} />}
            {draft && <PersonaPicker choice={persona} className="pb-3.5" />}
            {canPrepareSessions && draft && runner === null && (
              <div className="pb-3.5">
                <RunnerFallback support={support} />
              </div>
            )}
            {batchRun.error && (
              <p className="pb-3.5 text-sm text-destructive" role="alert">
                {batchRun.error}
              </p>
            )}
            <SessionBuild
              project={project}
              backlogItems={backlog.data?.items}
              backlogLoading={backlog.isLoading}
              backlogError={backlog.isError || (backlog.fetchStatus === "paused" && backlog.data === undefined)}
              backlogQuery={backlog}
              agenda={draft}
              entries={entries}
              canManageAgenda={canManageAgenda}
              canPrepare={canPrepareSessions}
              sessionWords={sessionWords}
              typicalItemSeconds={session.data?.typicalItemSeconds ?? null}
              locked={locked}
            />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}

/** `/grooming/:project/session/:batchId`: a run's Workspace and Agenda, with a way back to the build step. */
function SessionRunStep({ project, batchId }: { project: string; batchId: string }) {
  const { runner } = useRunnerBridge();
  // In the app, runner events drive the page; a browser polls while the batch runs.
  const batch = useGroomingBatch(batchId, { poll: runner === null });
  const session = useProjectSession(project);
  useQueueSettledRefresh(project, batchId);
  const { data: access } = useToolboxAccess();
  const canPresent = canOn(access, project, "present");
  const patError = getAdoPatErrorCode(batch.error);
  const { presenting, openPresent, closePresent } = useSessionPresent(canPresent && (batch.data?.items.length ?? 0) > 0);
  // Like the build step: an agenda without its own date is for the next meeting.
  const sessionDate = batch.data
    ? (batch.data.sessionDate ?? (batch.data.mode === "agenda" ? (session.data?.nextMeetingDate ?? null) : null))
    : null;

  const back = (
    <Link
      to={groomingPath(project, "session")}
      className="inline-flex h-[30px] items-center rounded-lg px-1.5 text-muted-foreground hover:bg-[#141417] hover:text-foreground"
      aria-label="Back to the agenda"
      data-testid="back-to-build"
    >
      <ArrowLeft className="h-[15px] w-[15px]" aria-hidden="true" />
    </Link>
  );

  if (patError) return <AdoPatCallout code={patError} />;
  if (batch.isLoading) return <Loading label="Loading the run…" />;
  if (batch.isError || !batch.data) {
    const code = groomingApiErrorCode(batch.error);
    return (
      <div className="flex items-center gap-2">
        {back}
        <p className="text-sm text-destructive" role="alert">
          {code === "FORBIDDEN" || code === "GROOMING_BATCH_FORBIDDEN"
            ? "Only the person who made this run, Project Managers and Admins can open it."
            : code === "NOT_FOUND"
              ? "This run was not found."
              : "The run could not be loaded. Try again in a moment."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="session-run">
      <BatchWorkspace
        project={project}
        batch={batch.data}
        back={back}
        headerActions={
          canPresent ? (
            <PresentButton size="sm" onPresent={openPresent} disabled={batch.data.items.length === 0} />
          ) : undefined
        }
        sessionDate={sessionDate}
      />
      {presenting && <AgendaPresent project={project} batch={batch.data} sessionDate={sessionDate} onClose={closePresent} />}
    </div>
  );
}

/** `/grooming/:project/session` (build step) and `/grooming/:project/session/:batchId` (run step). */
export function GroomingSessionPage() {
  const { project = "", batchId } = useParams();
  usePageTitle("Session");
  if (batchId) return <SessionRunStep project={project} batchId={batchId} />;
  return <SessionBuildStep project={project} />;
}
